import type { BaseModelAttributes } from './base';
import { Resolution, AspectRatio, I18nLabels } from './common';
import { TaskStatus, TaskStatusLabels } from './taskStatus';
import { Locale } from './locale';

// ⭐ 统一使用 TaskStatus，保留类型别名以向后兼容
export type VideoGenerationStatus = TaskStatus;
export const VideoGenerationStatus = TaskStatus;

// ⭐ 使用统一的状态标签
export const VideoGenerationStatusLabels: I18nLabels<VideoGenerationStatus> = TaskStatusLabels;

export enum VideoGenerationType {
  TEXT_TO_VIDEO = 'text2video',
  IMAGE_TO_VIDEO = 'image2video',
  KEYFRAME_TO_VIDEO = 'keyframe2video',
  MULTI_IMAGE_TO_VIDEO = 'multi_image2video'
}

export const VideoGenerationTypeLabels: I18nLabels<VideoGenerationType> = {
  [VideoGenerationType.TEXT_TO_VIDEO]: { [Locale.ZH_CN]: '文生视频', [Locale.EN_US]: 'Text to Video' },
  [VideoGenerationType.IMAGE_TO_VIDEO]: { [Locale.ZH_CN]: '图生视频', [Locale.EN_US]: 'Image to Video' },
  [VideoGenerationType.KEYFRAME_TO_VIDEO]: { [Locale.ZH_CN]: '首尾帧', [Locale.EN_US]: 'Keyframe' },
  [VideoGenerationType.MULTI_IMAGE_TO_VIDEO]: { [Locale.ZH_CN]: '多图视频', [Locale.EN_US]: 'Multi-Image Video' },
};

export interface IVideoGeneration extends BaseModelAttributes {
  prompt: string;
  modelId: string;
  type: VideoGenerationType;
  duration: number; // 视频时长（秒）
  resolution: Resolution;
  aspectRatio: AspectRatio;
  referenceImages: string[]; // 参考图片URL数组（图片生成视频时使用，1-4张）
  referenceAudio?: string; // 参考音频URL（可选）
  status: VideoGenerationStatus;
  taskId?: string;
  resultMessage?: string;
  resultVideoUrl?: string;
  submittedAt: Date;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateVideoGenerationRequest {
  prompt: string;
  modelId: string;
  type: VideoGenerationType;
  duration: number;
  resolution: Resolution;
  aspectRatio: AspectRatio;
  referenceImages: string[];
  referenceAudio?: string;
}

export interface UpdateVideoGenerationRequest {
  prompt?: string;
  modelId?: string;
  type?: VideoGenerationType;
  duration?: number;
  resolution?: Resolution;
  aspectRatio?: AspectRatio;
  referenceImages?: string[];
  referenceAudio?: string;
  status?: VideoGenerationStatus;
  taskId?: string;
  resultMessage?: string;
  resultVideoUrl?: string;
}

export interface VideoGenerationRequest {
  mode: VideoGenerationType;
  prompt: string;
  negativePrompt?: string;

  // 图片相关（根据模式使用）
  imageUrls?: string[];     // 图片URLs：单图/多图/首尾帧

  // 音频相关
  audioUrl?: string;        // 参考音频URL

  // 视频相关
  videoUrl?: string;        // 参考视频URL

  // 视频参数
  duration?: number;        // 时长（秒）
  resolution?: Resolution;  // 分辨率
  size?: string;           // 尺寸（如 "1280*720"）
  structure?: 'single' | 'multi'; // 镜头类型
  promptExtend?: boolean;   // 提示词扩展

  // 模型相关
  // model?: string;
  modelId: string;

  // 扩展参数
  [key: string]: any;
}

export interface VideoGenerationResponse {
  videoUrl: string;
  taskId?: string;
  duration?: number;
  resolution?: string;
}
export interface VideoGenerationQuery {
  search?: string;
  userId?: string;
  status?: VideoGenerationStatus;
  type?: VideoGenerationType;
  modelId?: string;
}
